import { Injectable } from '@angular/core';
import { TransformEmailListItemContract } from '@app-types/api/email';
import {
  BaseGetByEmailFileIdRequest,
  BaseGetByFileIdRequest,
} from '@app-types/base/base';
import { EmailsService } from '@app-services/emails/emails.service';
import { EmailClient } from '@app-services/api/clients/email-client';
import { FileClient } from '@app-services/api/clients/file.client';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { MatchError } from '@app-services/errors/error-matcher';
import { EmailValidationState } from '@app-types/enums/email.validation-state';
import { SnackbarService } from '@app-services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { EmailsScrollService } from '@app-services/emails/emails-scroll.service';
import { EmailState } from '@app-types/enums/email-state';
import { EmailActionsContextService } from './email-actions-context.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { EmailTab } from '@app-types/enums/email-tab';
import { MailFolderType } from '@app-types/enums/mail-folder.type';

@Injectable({
  providedIn: 'root',
})
export class ActionsService {
  private readonly emailsService: EmailsService;
  private readonly emailClient: EmailClient;
  private readonly matchError: MatchError;
  private readonly fileClient: FileClient;
  private readonly tempFileClient: TempFileClient;
  private readonly emailsScrollService: EmailsScrollService;
  public readonly snackbarHelper: SnackbarService;
  public readonly userWorkspaceService: UserWorkspaceService;
  public readonly translate: TranslateService;
  public readonly emailsPageStateService: EmailsPageStateService;

  constructor(private context: EmailActionsContextService) {
    this.emailsService = context.emailsService;
    this.emailClient = context.emailClient;
    this.matchError = context.matchError;
    this.fileClient = context.fileClient;
    this.tempFileClient = context.tempFileClient;
    this.emailsScrollService = context.emailsScrollService;
    this.snackbarHelper = context.snackbarHelper;
    this.userWorkspaceService = context.userWorkspaceService;
    this.translate = context.translate;
    this.emailsPageStateService = context.emailsPageStateService;
  }

  public async downloadSingleEML(id?: number): Promise<void> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    id &&
      this.emailsPageStateService.changeDataTable(
        [id],
        'isDownloadingSingleEML',
        true
      );

    !id &&
      this.emailsPageStateService.updateLoadingButtons({
        isDownloadingSingleEML: true,
      });

    try {
      const fileResponse = await this.emailClient.singleDownload({
        emailIds: ids,
      });

      const request =
        ids.length === 1
          ? new BaseGetByEmailFileIdRequest(fileResponse.fileId, ids[0])
          : new BaseGetByFileIdRequest(fileResponse.fileId);

      if (ids.length === 1) {
        await this.fileClient.download(request as BaseGetByEmailFileIdRequest);
      } else {
        await this.tempFileClient.download(request);
      }
    } catch (error) {
      this.matchError.errorHandler(error);
    } finally {
      id &&
        this.emailsPageStateService.changeDataTable(
          [id],
          'isDownloadingSingleEML',
          false
        );
      !id &&
        this.emailsPageStateService.updateLoadingButtons({
          isDownloadingSingleEML: false,
        });
    }
  }

  public async downloadEML(id?: number): Promise<void> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    id &&
      this.emailsPageStateService.changeDataTable(
        [id],
        'isDownloadingEML',
        true
      );
    !id &&
      this.emailsPageStateService.updateLoadingButtons({
        isDownloadingEML: true,
      });
    try {
      const fileResponse = await this.emailClient.download({ emailIds: ids });
      const request = new BaseGetByFileIdRequest(fileResponse.fileId);
      await this.tempFileClient.download(request);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      id &&
        this.emailsPageStateService.changeDataTable(
          [id],
          'isDownloadingEML',
          false
        );
      !id &&
        this.emailsPageStateService.updateLoadingButtons({
          isDownloadingEML: false,
        });
    }
  }

  updateStateAfterValidated(ids: number[]): void {
    if (
      this.emailsPageStateService.searchParams.validated?.length === 1 &&
      this.emailsPageStateService.searchParams.validated?.[0] ===
        EmailValidationState.NotValidated
    ) {
      this.emailsPageStateService.filterDataTable(ids);

      this.emailsScrollService.updateScrollConfig({
        offset: this.emailsScrollService.offset() - ids.length,
      });

      // this.emailsService.reloadDataOnDemand();
    } else {
      this.emailsPageStateService.changeDataTable(
        ids,
        'validationState',
        EmailValidationState.Validated
      );
    }

    ids.map(el => this.emailsPageStateService.selection.deselect(el));
  }

  async setValidated(emailId?: number): Promise<void> {
    const ids = emailId
      ? [emailId]
      : this.emailsPageStateService.selection.selected;
    try {
      !emailId &&
        this.emailsPageStateService.updateLoadingButtons({
          isLoadingValidate: true,
        });

      !!emailId &&
        this.emailsPageStateService.changeDataTable(
          ids,
          'isLoadingValidate',
          true
        );

      await this.emailClient.setValidated({
        emailsId: ids,
        validated: EmailValidationState.Validated,
      });

      const selectedEmails = this.emailsPageStateService.dataSource.data.filter(
        el => ids.includes(el.emailId)
      );

      const isValidateTab =
        this.emailsPageStateService.infoOpenedMailAccount?.tab ===
        EmailTab.NotValidatedUnreadCount;

      selectedEmails.forEach(el => {
        if (!el.seen && isValidateTab) {
          this.userWorkspaceService.updateEmailTabBadges(
            EmailTab.NotValidatedUnreadCount,
            false
          );
          this.userWorkspaceService.updateEmailTabBadges(
            EmailTab.NewAndValidatedUnreadCount,
            true
          );
        }
      });

      this.updateStateAfterValidated(ids);

      this.snackbarHelper.openSnackBar(
        this.translate.instant('PECWasValidatedSuccessfully')
      );
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      !!emailId &&
        this.emailsPageStateService.changeDataTable(
          ids,
          'isLoadingValidate',
          false
        );
      !emailId &&
        this.emailsPageStateService.updateLoadingButtons({
          isLoadingValidate: false,
        });
    }
  }

  public async retrySend(id?: number): Promise<void> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    try {
      await this.emailClient.retrySend({ list: ids });
      this.emailsPageStateService.changeObjectDataTable(ids, {
        state: EmailState.Sending,
        hasRetrySending: false,
        hasMoveToDraft: false,
        hasMeta: false,
        isRowWithError: false,
      });
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async setSeen(
    seen: boolean,
    email?: TransformEmailListItemContract
  ): Promise<any> {
    const ids = email
      ? [email.emailId]
      : this.emailsPageStateService.selection.selected;
    try {
      await this.emailClient.setSeen({ emailIds: ids, seen });
      this.userWorkspaceService.updateUnreadList(ids, seen, email);
      this.emailsPageStateService.changeDataTable(ids, 'seen', seen);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async restore(folderId: number, id?: number[]): Promise<any> {
    const ids = id ? id : this.emailsPageStateService.selection.selected;
    try {
      await this.emailClient.restore({ emailIds: ids, mailFolderId: folderId });
      this.userWorkspaceService.updateMoveList(ids, folderId);
      this.emailsService.updateDataOnEmailCountChange(ids);
      // this.emailsService.reloadDataOnDemand();
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  async setSpam(id?: number): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    try {
      await this.emailClient.setIsSpam({ emailIds: ids });

      const folderSpamId = this.userWorkspaceService.userFolders.find(
        el =>
          el.mailAccountId ===
            this.emailsPageStateService.infoOpenedMailAccount?.acc &&
          el.folderType === MailFolderType.Spam
      )?.mailFolderId;

      this.userWorkspaceService.updateMoveList(ids, folderSpamId);
      this.emailsService.updateDataOnEmailCountChange(ids);
      // this.emailsService.reloadDataOnDemand();
    } catch (e: any) {
      this.matchError.openErrorDialog(
        'errorPopupTitle',
        e?.Description || 'cannotSetSpamSelectedErrorPopupSubtitle'
      );
      this.matchError.logError(e);
    }
  }

  async setNotSpam(ids: number[], folderId: number): Promise<any> {
    try {
      await this.emailClient.setNotSpam({
        emailIds: ids,
        mailFolderId: folderId,
      });
      this.userWorkspaceService.updateMoveList(ids, folderId);
      this.emailsService.updateDataOnEmailCountChange(ids);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  public async moveToDraft(id: number): Promise<void> {
    try {
      await this.emailClient.moveToDraft({ emailId: id });
      const foldrDraft = this.userWorkspaceService.userFolders.find(
        el =>
          el.mailAccountId ===
            this.emailsPageStateService.infoOpenedMailAccount?.acc &&
          el.folderType === MailFolderType.Draft
      )?.mailFolderId;
      this.userWorkspaceService.updateMoveList([id], foldrDraft);
      this.emailsService.updateDataOnEmailCountChange([id]);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }

  updateLoading(value: boolean, ids?: number[]): void {
    if (ids?.length) {
      this.emailsPageStateService.changeObjectDataTable(ids, {
        isStarredLoading: value,
      });
    } else {
      this.emailsPageStateService.updateLoadingButtons({
        isStarred: value,
      });
    }
  }

  async addEmailToStar(id?: number): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    try {
      const currentEmails = this.emailsPageStateService.dataSource.data.filter(
        el => ids.includes(el.emailId)
      );
      const isResetStar = currentEmails.every(el => el.isStarred);
      this.updateLoading(true, id ? ids : undefined);
      isResetStar
        ? await this.emailClient.reSetStarred({ list: ids })
        : await this.emailClient.setStarred({ list: ids });

      const isStarFolder =
        this.emailsPageStateService.infoOpenedMailAccount?.type ===
        MailFolderType.View;
      const folderStarId = this.userWorkspaceService.userFolders.find(
        folder =>
          folder.mailAccountId ===
            this.emailsPageStateService.infoOpenedMailAccount?.acc &&
          folder.folderType === MailFolderType.View
      )?.mailFolderId;

      const count = currentEmails.filter(el =>
        isResetStar ? !el.seen : !el.seen && !el.isStarred
      ).length;

      this.emailsPageStateService.infoOpenedMailAccount?.acc &&
        folderStarId &&
        this.userWorkspaceService.updateUnread(
          folderStarId,
          this.emailsPageStateService.infoOpenedMailAccount?.acc,
          !isResetStar,
          count,
          false,
          false
        );

      if (isStarFolder) {
        this.emailsService.updateDataOnEmailCountChange(ids);
      } else {
        this.emailsPageStateService.changeObjectDataTable(ids, {
          isStarred: !isResetStar,
        });
      }
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.updateLoading(false, id ? ids : undefined);
    }
  }
}
